import { useState, useEffect } from 'react';
import AnalyticsService from '../services/AnalyticsService';
import PatientService from '../services/PatientService';

/**
 * Custom hook for real-time analytics data
 * Polls the backend for updated analytics every 30 seconds
 */
export const useRealtimeAnalytics = (filters = {}, pollInterval = 30000) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchData = async () => {
    try {
      setError(null);
      const analyticsData = await AnalyticsService.getDashboardAnalytics(filters);
      const queueStats = await AnalyticsService.getQueueStatistics();
      
      setData({
        ...analyticsData.data,
        queueStats,
        realtime: {
          lastUpdated: new Date(),
          pollInterval
        }
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching realtime analytics:', err);
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchData();
  }, [JSON.stringify(filters)]);

  // Set up polling
  useEffect(() => {
    if (pollInterval <= 0) return;

    const interval = setInterval(fetchData, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval, JSON.stringify(filters)]);

  const refresh = () => {
    setLoading(true);
    fetchData();
  };

  return {
    data,
    loading,
    error,
    lastUpdated,
    refresh
  };
};

/**
 * Custom hook for patient queue realtime updates
 */
export const useRealtimeQueue = (eventId = null, pollInterval = 15000) => {
  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQueueData = async () => {
    try {
      setError(null);
      const response = eventId 
        ? await PatientService.getPatientQueue(eventId, { statsOnly: true })
        : await PatientService.getAllEventsPatientQueue({ statsOnly: true });
      
      setQueueData({
        ...response,
        lastUpdated: new Date()
      });
    } catch (err) {
      console.error('Error fetching queue data:', err);
      setError(err.message || 'Failed to fetch queue data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueueData();
  }, [eventId]);

  useEffect(() => {
    if (pollInterval <= 0) return;

    const interval = setInterval(fetchQueueData, pollInterval);
    return () => clearInterval(interval);
  }, [pollInterval, eventId]);

  return {
    queueData,
    loading,
    error,
    refresh: fetchQueueData
  };
};

/**
 * Custom hook for live statistics
 */
export const useLiveStatistics = () => {
  const [stats, setStats] = useState({
    activeUsers: 0,
    ongoingScreenings: 0,
    todayScreenings: 0,
    avgResponseTime: 0
  });

  const [isLive, setIsLive] = useState(false);

  const startLiveUpdates = () => {
    setIsLive(true);
    // Simulate live updates
    const interval = setInterval(() => {
      setStats(prev => ({
        activeUsers: Math.max(0, prev.activeUsers + Math.floor(Math.random() * 3) - 1),
        ongoingScreenings: Math.max(0, prev.ongoingScreenings + Math.floor(Math.random() * 2) - 1),
        todayScreenings: prev.todayScreenings + Math.floor(Math.random() * 2),
        avgResponseTime: Math.max(10, prev.avgResponseTime + Math.floor(Math.random() * 10) - 5)
      }));
    }, 2000);

    return () => {
      clearInterval(interval);
      setIsLive(false);
    };
  };

  const stopLiveUpdates = () => {
    setIsLive(false);
  };

  useEffect(() => {
    // Initialize with some base values
    setStats({
      activeUsers: 8,
      ongoingScreenings: 3,
      todayScreenings: 42,
      avgResponseTime: 28
    });
  }, []);

  return {
    stats,
    isLive,
    startLiveUpdates,
    stopLiveUpdates
  };
};
